import React, { Fragment, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import PropTypes from 'prop-types'
import { XIcon } from '@heroicons/react/outline'
import InputText from '../../../components/InputText/InputText'
import Button from '../../../components/Button/Button'
import {
    initialValuesCustomerAlias,
    validationSchemaCustomerAlias,
} from '../../../forms/UpdateAssetForm'
import { useFormik } from 'formik'
import useGlobalLoading from '../../../hooks/useGlobalLoading'
import useAlert from '../../../hooks/useAlert'

const UpdateAssetNameModal = ({
    isOpen,
    setIsOpen,
    assetCustomerAlias,
    onSave,
}) => {
    const closeModal = () => {
        setIsOpen(false)
    }
    const { handleChangeAlert } = useAlert()

    const { isLoading } = useGlobalLoading()

    const onSaveSuccess = () => {
        setIsOpen(false)
        handleChangeAlert({
            type: 'success',
            title: 'Customer Unit Number Request Received',
            message:
                'Your customer unit number update request has been received, and will be processed within 1 to 2 business days.',
        })
    }

    const onSaveError = error => {
        setIsOpen(false)
        console.log(error?.response?.data?.message)
        const message = error?.response?.data?.message
        handleChangeAlert({
            title: null,
            message: message,
            type: 'error',
        })
    }

    const {
        handleSubmit,
        getFieldProps,
        touched,
        errors,
        setFieldValue,
        isValid,
        isSubmitting,
        values,
    } = useFormik({
        initialValues: initialValuesCustomerAlias(assetCustomerAlias),
        validationSchema: validationSchemaCustomerAlias,
        onSubmit: formValues => {
            return onSave(
                formValues,
                onSaveSuccess,
                'equipmentName',
                onSaveError,
            )
        },
    })

    useEffect(() => {
        if (assetCustomerAlias) {
            setFieldValue('customer_alias', assetCustomerAlias)
        }
    }, [assetCustomerAlias])

    return (
        <>
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="relative z-200"
                    onClose={closeModal}
                >
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-modalBackground" />
                    </Transition.Child>

                    <form
                        onSubmit={handleSubmit}
                        className="fixed inset-0 overflow-y-auto"
                    >
                        <div className="flex min-h-full items-center justify-center text-center md:p-0 p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-3xl bg-white md:p-12 p-4 text-center transition-all relative flex flex-col items-center justify-start gap-y-6">
                                    <XIcon
                                        className="w-4 h-4 stroke-current stroke-2 absolute text-textDarkerGray top-6 right-6 cursor-pointer"
                                        onClick={closeModal}
                                    />
                                    <Dialog.Title
                                        as="p"
                                        className="text-lg text-black90 font-semibold"
                                    >
                                        Update Customer Unit Number
                                    </Dialog.Title>
                                    <InputText
                                        label="Customer Unit Number"
                                        placeholder="Enter asset name"
                                        {...getFieldProps('customer_alias')}
                                        error={
                                            touched['customer_alias'] &&
                                            errors['customer_alias']
                                        }
                                    />

                                    <div className="w-full h-auto flex flex-row items-center justify-between gap-x-6">
                                        <Button
                                            text="Cancel"
                                            className="flex-grow w-full"
                                            variant="tertiary"
                                            onClick={closeModal}
                                            type="button"
                                        />
                                        <Button
                                            text="Update Request"
                                            className="flex-grow w-full"
                                            variant="secondary"
                                            type="submit"
                                            disabled={
                                                !isValid ||
                                                isSubmitting ||
                                                isLoading ||
                                                assetCustomerAlias ===
                                                    values['notes']
                                            }
                                        />
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </form>
                </Dialog>
            </Transition>
        </>
    )
}

UpdateAssetNameModal.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    assetCustomerAlias: PropTypes.string,
    onSave: PropTypes.func,
}

export default UpdateAssetNameModal
